import axios from '../axios';
import { GET_RESTAURANT_ORDERS, GET_NEXT_DAY_PROMO_STORE_ORDERS } from '../ApiUrl';

const DEFAULT_TIMEZONE = 'America/Chicago';
const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;

/**
 * POST /GetRestaurantOrders
 * Fetches paginated active orders for a regional branch on a delivery date.
 *
 * @param {Object} params
 * @param {string} params.date - MM/DD/YYYY or YYYY-MM-DD
 * @param {string} params.regionalBranch - matched to kitchen_managingBranchName (trimmed + lowercased)
 * @param {number|string} [params.page=1] - min 1
 * @param {number|string} [params.limit=20] - min 1, max 100
 * @param {string} [params.timeZone='America/Chicago'] - IANA timezone for dispatch_sort_time
 */
export const getActiveOrdersAPI = async ({
  date,
  regionalBranch,
  page = 1,
  limit = DEFAULT_LIMIT,
  timeZone = DEFAULT_TIMEZONE,
}) => {
  try {
    const branch = String(regionalBranch ?? '')
      .trim()
      .toLowerCase();
    if (!branch) {
      return { hasError: true, message: 'Regional branch is required.' };
    }

    const dateStr = String(date ?? '').trim();
    if (!dateStr) {
      return { hasError: true, message: 'Delivery date is required.' };
    }

    const safePage = Math.max(1, parseInt(String(page), 10) || 1);
    let safeLimit = parseInt(String(limit), 10) || DEFAULT_LIMIT;
    if (safeLimit < 1) safeLimit = 1;
    if (safeLimit > MAX_LIMIT) safeLimit = MAX_LIMIT;

    const body = {
      date: dateStr,
      regionalBranch: branch,
      page: safePage,
      limit: safeLimit,
    };

    const tz = String(timeZone ?? '').trim();
    if (tz) {
      body.timeZone = tz;
    }

    const response = await axios.post(GET_RESTAURANT_ORDERS, body);
    return response;
  } catch (error) {
    console.log('getActiveOrdersAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to load orders.' };
  }
};

/**
 * POST /GetNextDayPromoStoreOrders
 * Fetches next-day promotion orders for today and tomorrow (fixed date pair).
 *
 * @param {Object} params
 * @param {string[]} params.date - [today MM/DD/YYYY, tomorrow YYYY-MM-DD]
 * @param {string} params.regionalBranch
 * @param {number|string} [params.page=1]
 * @param {number|string} [params.limit=100]
 * @param {string} [params.timeZone='America/Chicago']
 */
export const getNextDayPromoStoreOrdersAPI = async ({
  date,
  regionalBranch,
  page = 1,
  limit = MAX_LIMIT,
  timeZone = DEFAULT_TIMEZONE,
}) => {
  try {
    const branch = String(regionalBranch ?? '')
      .trim()
      .toLowerCase();
    if (!branch) {
      return { hasError: true, message: 'Regional branch is required.' };
    }

    const dates = Array.isArray(date) ? date.filter((d) => String(d ?? '').trim()) : [];
    if (dates.length < 2) {
      return { hasError: true, message: 'Today and tomorrow dates are required.' };
    }

    const safePage = Math.max(1, parseInt(String(page), 10) || 1);
    let safeLimit = parseInt(String(limit), 10) || MAX_LIMIT;
    if (safeLimit < 1) safeLimit = 1;
    if (safeLimit > MAX_LIMIT) safeLimit = MAX_LIMIT;

    const body = {
      date: dates,
      regionalBranch: branch,
      page: safePage,
      limit: safeLimit,
    };

    const tz = String(timeZone ?? '').trim();
    if (tz) {
      body.timeZone = tz;
    }

    const response = await axios.post(GET_NEXT_DAY_PROMO_STORE_ORDERS, body);
    return response;
  } catch (error) {
    console.log('getNextDayPromoStoreOrdersAPI catch', error);
    return {
      hasError: true,
      message: error?.message || 'Failed to load next day promotion orders.',
    };
  }
};
